import { motion, type Transition } from 'motion/react';
import type { ReactNode } from 'react';

import { cn } from '../../lib/cn.js';
import { EASE_OUT_QUINT } from '../motion.js';

/** As quatro pontas, cada quadradinho de 5px centrado no canto do filete de 1px. */
const PONTAS = [
  '-top-[2px] -left-[2px]',
  '-top-[2px] -right-[2px]',
  '-bottom-[2px] -left-[2px]',
  '-bottom-[2px] -right-[2px]',
] as const;

/**
 * Os quatro lados, cada um saindo de uma ponta, todos no mesmo sentido: a
 * esquerda desce, a de baixo vai para a direita, a direita sobe e a de cima
 * volta para a esquerda. Cada lado cresce a partir da ponta de onde sai
 * (`origin-*`), e chega à ponta seguinte quando os outros três chegam às deles.
 */
const LADOS = [
  { classe: 'inset-y-0 left-0 w-px origin-top', eixo: 'y' },
  { classe: 'inset-x-0 bottom-0 h-px origin-left', eixo: 'x' },
  { classe: 'inset-y-0 right-0 w-px origin-bottom', eixo: 'y' },
  { classe: 'inset-x-0 top-0 h-px origin-right', eixo: 'x' },
] as const;

/**
 * Quanto a moldura leva para se desenhar, em segundos: as pontas surgem, e os
 * lados partem delas depois que assentaram. A do banner é o enfeite da tela e
 * desenha devagar; a do marcador da linha do tempo é pequena, se repete a cada
 * relatório e desenha depressa.
 */
const RITMOS = {
  largo: { pontas: 0.3, partida: 0.32, lados: 0.75 },
  curto: { pontas: 0.2, partida: 0.16, lados: 0.4 },
} as const;

export type RitmoDaMoldura = keyof typeof RITMOS;

/** Quando a moldura termina de se desenhar — para o que vem depois dela, como o fio da linha do tempo. */
export const fimDaMoldura = (ritmo: RitmoDaMoldura): number =>
  RITMOS[ritmo].partida + RITMOS[ritmo].lados;

/**
 * A moldura de prancha técnica: um filete fino em volta do conteúdo, afastado
 * dele, com um quadradinho cheio em cada ponta. Em cinza — filete `ink-3` a
 * 35%, pontas `ink-3` —, para ser detalhe e não moldura de quadro. Enquadra o
 * banner da obra e cada marcador da linha do tempo.
 *
 * Não aparece pronta: primeiro surgem as quatro pontas, e então os quatro lados
 * partem delas ao mesmo tempo, no mesmo sentido, e se encontram nas pontas
 * seguintes. Com movimento reduzido, as pontas só aparecem e os lados já vêm
 * inteiros.
 */
export function Moldura({
  children,
  className,
  ritmo = 'largo',
  atraso = 0,
}: {
  children: ReactNode;
  /** O afastamento entre o filete e o que ele enquadra (`p-1.5`, `p-1`). */
  className?: string | undefined;
  ritmo?: RitmoDaMoldura | undefined;
  /** Segundos antes de começar: na linha do tempo, os marcadores desenham em fila. */
  atraso?: number | undefined;
}) {
  const tempo = RITMOS[ritmo];
  const pontas: Transition = { delay: atraso, duration: tempo.pontas, ease: EASE_OUT_QUINT };
  const lados: Transition = {
    delay: atraso + tempo.partida,
    duration: tempo.lados,
    ease: EASE_OUT_QUINT,
  };

  return (
    <div className={cn('relative', className)}>
      {LADOS.map((lado) => (
        <motion.span
          key={lado.classe}
          aria-hidden
          className={cn('pointer-events-none absolute bg-ink-3/35', lado.classe)}
          initial={lado.eixo === 'x' ? { scaleX: 0 } : { scaleY: 0 }}
          animate={lado.eixo === 'x' ? { scaleX: 1 } : { scaleY: 1 }}
          transition={lados}
        />
      ))}
      {PONTAS.map((ponta) => (
        <motion.span
          key={ponta}
          aria-hidden
          className={cn('pointer-events-none absolute size-[5px] bg-ink-3', ponta)}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={pontas}
        />
      ))}
      {children}
    </div>
  );
}
